import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getBranchById,
  getBranchProducts,
  getBranchStaff,
  assignProductsToBranch,
  removeProductFromBranch,
  updateProductAvailability,
  updateBranchProductPrice,
  transferStaff,
  updateBranch,
  deleteBranch,
  getBranches,
  setMainBranch
} from '../../api/branches';
import { getProducts } from '../../api/products';
import { usersAPI } from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  Building2,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Users,
  Package,
  Edit2,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  DollarSign,
  Search,
  RefreshCw,
  UserPlus,
  Grid,
  List,
  AlertTriangle,
  X,
  Star
} from 'lucide-react';
import EditBranchProductModal from '../../components/branches/EditBranchProductModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BranchDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Products state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Staff state
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit product state
  const [editProduct, setEditProduct] = useState(null);

  // Close branch state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [targetBranchId, setTargetBranchId] = useState('');
  const [closingBranch, setClosingBranch] = useState(false);
  const [settingMain, setSettingMain] = useState(false);

  const canManage = ['super_admin', 'system_admin', 'retail_admin', 'branch_admin'].includes(
    currentUser?.role
  );

  useEffect(() => {
    fetchBranch();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'staff') {
      fetchStaff();
    }
  }, [activeTab, id]);

  const fetchBranch = async () => {
    try {
      setLoading(true);
      const response = await getBranchById(id);
      setBranch(response.data);
    } catch (error) {
      toast.error('Failed to load branch details');
      navigate('/branches');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await getBranchProducts(id, { limit: 100 });
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Failed to load branch products');
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      setStaffLoading(true);
      const response = await getBranchStaff(id, { limit: 100 });
      setStaff(response.data || []);
    } catch (error) {
      toast.error('Failed to load branch staff');
    } finally {
      setStaffLoading(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    if (!window.confirm('Remove this product from the branch?')) return;
    try {
      await removeProductFromBranch(id, productId);
      toast.success('Product removed from branch');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to remove product');
    }
  };

  const handleToggleAvailability = async (productId, currentAvailability) => {
    try {
      await updateProductAvailability(id, productId, !currentAvailability);
      toast.success(`Product ${!currentAvailability ? 'enabled' : 'disabled'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const handleUpdateBranch = async (data) => {
    try {
      await updateBranch(id, data);
      toast.success('Branch updated');
      setShowEditModal(false);
      fetchBranch();
    } catch (error) {
      toast.error('Failed to update branch');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchAvailableBranches = async () => {
    try {
      const response = await getBranches({});
      // Handle both response shapes: { data: [...] } or direct array
      let branches = [];
      if (Array.isArray(response)) {
        branches = response;
      } else if (Array.isArray(response?.data)) {
        branches = response.data;
      } else if (Array.isArray(response?.branches)) {
        branches = response.branches;
      }
      // Filter out current branch and inactive branches
      const filtered = branches.filter((b) => b.id !== parseInt(id) && b.is_active !== false);
      console.log(
        'Available branches for transfer:',
        filtered.length,
        filtered.map((b) => b.name)
      );
      setAvailableBranches(filtered);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load available branches for transfer');
    }
  };

  const handleOpenCloseModal = () => {
    fetchAvailableBranches();
    setTargetBranchId('');
    setShowCloseModal(true);
  };

  const handleCloseBranch = async () => {
    if (branch.staff_count > 0) {
      toast.error('Cannot close branch with assigned staff. Please transfer staff first.');
      return;
    }

    if (availableBranches.length > 1 && !targetBranchId) {
      toast.error('Please select a target branch.');
      return;
    }

    setClosingBranch(true);
    try {
      const transferTo = availableBranches.length === 1 ? availableBranches[0].id : targetBranchId;
      await deleteBranch(id, transferTo || null);
      toast.success('Branch closed successfully!');
      navigate('/branches');
    } catch (error) {
      if (error.response?.data?.code === 'TARGET_BRANCH_REQUIRED') {
        setAvailableBranches(error.response.data.availableBranches || []);
        toast.error('Please select a target branch to transfer stock.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to close branch');
      }
    } finally {
      setClosingBranch(false);
    }
  };

  const handleSetAsMain = async () => {
    if (
      !window.confirm(
        `Set "${branch.name}" as the main branch? This will remove main status from other branches.`
      )
    ) {
      return;
    }

    setSettingMain(true);
    try {
      await setMainBranch(id);
      toast.success(`"${branch.name}" is now the main branch`);
      fetchBranch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to set as main branch');
    } finally {
      setSettingMain(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading branch..." className="min-h-screen" />;
  }

  if (!branch) return null;

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'products', name: `Products (${branch.product_count || 0})` },
    { id: 'staff', name: `Staff (${branch.staff_count || 0})` }
  ];

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/branches')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-accent-gold/10 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-accent-gold" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-primary-black">{branch.name}</h1>
                    {branch.is_main && (
                      <span className="px-2 py-0.5 text-xs bg-accent-gold/20 text-accent-gold rounded-full">
                        Main
                      </span>
                    )}
                    {branch.is_active ? (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                        <XCircle className="h-3 w-3" /> Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-primary-slate">Code: {branch.code}</p>
                </div>
              </div>
            </div>
            {canManage && (
              <div className="flex items-center gap-2">
                {/* Set as Main button - only show for non-main branches */}
                {!branch.is_main && (
                  <button
                    onClick={handleSetAsMain}
                    disabled={settingMain}
                    className="flex items-center gap-2 px-4 py-2 border border-accent-gold text-accent-gold rounded-lg hover:bg-accent-gold/10 disabled:opacity-50"
                    title="Set this branch as the main/HQ branch"
                  >
                    <Star className="h-4 w-4" />
                    {settingMain ? 'Setting...' : 'Set as Main'}
                  </button>
                )}
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
                {/* Close Branch - disabled for main branch */}
                <button
                  onClick={handleOpenCloseModal}
                  disabled={branch.is_main}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    branch.is_main
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                  title={
                    branch.is_main
                      ? 'Cannot close main branch. Set another branch as main first.'
                      : 'Close this branch'
                  }
                >
                  <AlertTriangle className="h-4 w-4" />
                  Close Branch
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-accent-gold text-accent-gold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab branch={branch} formatCurrency={formatCurrency} />
        )}

        {activeTab === 'products' && (
          <ProductsTab
            products={products}
            loading={productsLoading}
            onRefresh={fetchProducts}
            onRemove={handleRemoveProduct}
            onToggleAvailability={handleToggleAvailability}
            onAddProducts={() => setShowAddProductModal(true)}
            onEditProduct={(product) => setEditProduct(product)}
            canManage={canManage}
            formatCurrency={formatCurrency}
          />
        )}

        {activeTab === 'staff' && (
          <StaffTab
            staff={staff}
            loading={staffLoading}
            onRefresh={fetchStaff}
            onTransfer={() => setShowTransferModal(true)}
            canManage={canManage}
          />
        )}
      </div>

      {/* Modals */}
      {showAddProductModal && (
        <AddProductModal
          branchId={id}
          existingProductIds={products.map((p) => p.product_id)}
          onClose={() => setShowAddProductModal(false)}
          onSuccess={() => {
            setShowAddProductModal(false);
            fetchProducts();
            fetchBranch();
          }}
        />
      )}

      {showTransferModal && (
        <TransferStaffModal
          branchId={id}
          branchName={branch.name}
          onClose={() => setShowTransferModal(false)}
          onSuccess={() => {
            setShowTransferModal(false);
            fetchStaff();
            fetchBranch();
          }}
        />
      )}

      {showEditModal && (
        <EditBranchModal
          branch={branch}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateBranch}
        />
      )}

      {editProduct && (
        <EditBranchProductModal
          product={editProduct}
          branchId={id}
          onClose={() => setEditProduct(null)}
          onSuccess={() => {
            setEditProduct(null);
            fetchProducts();
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Close Branch Confirmation Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Close Branch</h2>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600">
                  You are about to permanently close{' '}
                  <span className="font-semibold">{branch.name}</span>.
                </p>

                {branch.staff_count > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800 text-sm">
                      <strong>Warning:</strong> This branch has {branch.staff_count} staff
                      member(s). Please transfer all staff to another branch before closing.
                    </p>
                  </div>
                )}

                {availableBranches.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    {branch.product_count > 0 ? (
                      <p className="text-blue-800 text-sm mb-2">
                        <strong>Stock Transfer:</strong> All {branch.product_count} product(s) with
                        stock will be transferred to another branch.
                      </p>
                    ) : (
                      <p className="text-blue-800 text-sm mb-2">
                        <strong>Branch Consolidation:</strong> Select a target branch to consolidate
                        system data to.
                      </p>
                    )}

                    {availableBranches.length === 1 ? (
                      <p className="text-sm text-blue-700">
                        {branch.product_count > 0 ? 'Stock' : 'Data'} will be transferred to:{' '}
                        <strong>{availableBranches[0].name}</strong>
                      </p>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select target branch:
                        </label>
                        <select
                          value={targetBranchId}
                          onChange={(e) => setTargetBranchId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold"
                        >
                          <option value="">-- Select Branch --</option>
                          {availableBranches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name} ({b.code})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {availableBranches.length === 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">
                      <strong>Error:</strong> Cannot close the last remaining branch. At least one
                      branch must exist.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 mt-6 pt-4 border-t">
                {/* Show reason why button is disabled */}
                {branch.staff_count > 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    ⚠️ Transfer all staff before closing this branch.
                  </p>
                )}
                {availableBranches.length === 0 && branch.staff_count === 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    ⚠️ No other active branches available. Create another branch first.
                  </p>
                )}
                {availableBranches.length > 1 && !targetBranchId && branch.staff_count === 0 && (
                  <p className="text-sm text-amber-600 font-medium">
                    ⚠️ Please select a target branch for consolidation above.
                  </p>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowCloseModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={closingBranch}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseBranch}
                    disabled={
                      closingBranch ||
                      branch.staff_count > 0 ||
                      availableBranches.length === 0 ||
                      (availableBranches.length > 1 && !targetBranchId)
                    }
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {closingBranch ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Closing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        Close Branch
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ branch, formatCurrency }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Branch Info */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Branch Information</h3>
      <div className="space-y-3">
        {branch.address && (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
            <span>{branch.address}</span>
          </div>
        )}
        {branch.phone && (
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-gray-400" />
            <span>{branch.phone}</span>
          </div>
        )}
        {branch.email && (
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <span>{branch.email}</span>
          </div>
        )}
        {branch.manager_name && (
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-gray-400" />
            <span>
              Manager: <strong>{branch.manager_name}</strong>
            </span>
          </div>
        )}
      </div>
    </div>

    {/* Quick Stats */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Users className="h-5 w-5" />
            <span className="text-sm font-medium">Staff</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">{branch.staff_count || 0}</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Package className="h-5 w-5" />
            <span className="text-sm font-medium">Products</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{branch.product_count || 0}</p>
        </div>
      </div>
    </div>

    {/* Created Info */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">History</h3>
      <div className="space-y-2 text-sm text-gray-600">
        <p>Created: {new Date(branch.created_at).toLocaleDateString()}</p>
        {branch.created_by_name && <p>Created by: {branch.created_by_name}</p>}
        {branch.updated_at && (
          <p>Last updated: {new Date(branch.updated_at).toLocaleDateString()}</p>
        )}
      </div>
    </div>
  </div>
);

// Products Tab
const ProductsTab = ({
  products,
  loading,
  onRefresh,
  onRemove,
  onToggleAvailability,
  onAddProducts,
  onEditProduct,
  canManage,
  formatCurrency
}) => {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

  const filteredProducts = products.filter(
    (p) =>
      p.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.product_code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-accent-gold' : 'text-gray-500 hover:text-gray-700'}`}
              title="Table View"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-accent-gold' : 'text-gray-500 hover:text-gray-700'}`}
              title="Grid View"
            >
              <Grid className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onRefresh} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canManage && (
            <button
              onClick={onAddProducts}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
            >
              <Plus className="h-4 w-4" />
              Add Products
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-gold"></div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No products assigned to this branch</p>
        </div>
      ) : viewMode === 'table' ? (
        // Table View
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Global Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Local Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                {canManage && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr
                  key={product.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onEditProduct && onEditProduct(product)}
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium">{product.product_name}</p>
                      <p className="text-sm text-gray-500">{product.product_code}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">{formatCurrency(product.global_price)}</td>
                  <td className="px-6 py-4 text-sm">
                    {product.local_price ? (
                      <span className="text-green-600 font-medium">
                        {formatCurrency(product.local_price)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">{product.branch_stock || 0}</td>
                  <td className="px-6 py-4">
                    {product.is_available ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        Available
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                        Unavailable
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onEditProduct && onEditProduct(product)}
                          className="p-1 hover:bg-blue-50 rounded text-blue-600"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            onToggleAvailability(product.product_id, product.is_available)
                          }
                          className="p-1 hover:bg-gray-100 rounded"
                          title={product.is_available ? 'Disable' : 'Enable'}
                        >
                          {product.is_available ? (
                            <EyeOff className="h-4 w-4 text-gray-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-green-500" />
                          )}
                        </button>
                        <button
                          onClick={() => onRemove(product.product_id)}
                          className="p-1 hover:bg-red-50 rounded text-red-500"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Grid View
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              onClick={() => onEditProduct && onEditProduct(product)}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-100 hover:border-accent-gold/30 overflow-hidden"
            >
              {/* Card Header */}
              <div className="h-3 bg-gradient-to-r from-accent-gold to-yellow-500"></div>

              <div className="p-4">
                {/* Product Name & Code */}
                <div className="mb-3">
                  <h4 className="font-bold text-gray-900 truncate">{product.product_name}</h4>
                  <p className="text-sm text-gray-500">{product.product_code}</p>
                </div>

                {/* Price Info */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-500">Price</p>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(product.local_price || product.global_price)}
                    </p>
                    {product.local_price && (
                      <p className="text-xs text-gray-400 line-through">
                        {formatCurrency(product.global_price)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Stock</p>
                    <p
                      className={`text-lg font-bold ${(product.branch_stock || 0) > 0 ? 'text-blue-600' : 'text-red-500'}`}
                    >
                      {product.branch_stock || 0}
                    </p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  {product.is_available ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      Available
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                      Unavailable
                    </span>
                  )}
                  {canManage && (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() =>
                          onToggleAvailability(product.product_id, product.is_available)
                        }
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        title={product.is_available ? 'Disable' : 'Enable'}
                      >
                        {product.is_available ? (
                          <EyeOff className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-green-500" />
                        )}
                      </button>
                      <button
                        onClick={() => onRemove(product.product_id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Staff Tab
const StaffTab = ({ staff, loading, onRefresh, onTransfer, canManage }) => {
  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-primary-black',
      system_admin: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
      retail_admin: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
      branch_admin: 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white',
      branch_staff: 'bg-gradient-to-r from-emerald-500 to-green-600 text-white',
      admin: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
      staff: 'bg-gradient-to-r from-emerald-500 to-green-600 text-white'
    };
    const labels = {
      super_admin: 'Super Admin',
      system_admin: 'System Admin',
      retail_admin: 'Retail Admin',
      branch_admin: 'Branch Admin',
      branch_staff: 'Staff',
      admin: 'Admin',
      staff: 'Staff'
    };
    return (
      <span
        className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${styles[role] || 'bg-gray-200 text-gray-700'}`}
      >
        {labels[role] || role}
      </span>
    );
  };

  const getAvatarGradient = (index) => {
    const gradients = [
      'from-amber-400 to-orange-500',
      'from-blue-400 to-indigo-500',
      'from-emerald-400 to-teal-500',
      'from-purple-400 to-pink-500',
      'from-rose-400 to-red-500',
      'from-cyan-400 to-blue-500'
    ];
    return gradients[index % gradients.length];
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-primary-black">Branch Staff</h3>
          <p className="text-sm text-gray-500 mt-1">
            {staff.length} team member{staff.length !== 1 ? 's' : ''} assigned
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl shadow-sm transition-all hover:shadow"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canManage && (
            <button
              onClick={onTransfer}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-primary-black font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <UserPlus className="h-4 w-4" />
              Transfer Staff Here
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-accent-gold"></div>
          <p className="mt-4 text-gray-500">Loading staff...</p>
        </div>
      ) : staff.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-16 text-center border border-gray-100">
          <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Users className="h-10 w-10 text-gray-300" />
          </div>
          <h4 className="text-lg font-semibold text-gray-700 mb-2">No Staff Assigned</h4>
          <p className="text-gray-500 text-sm max-w-sm mx-auto">
            Transfer staff members to this branch to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {staff.map((member, index) => (
            <div
              key={member.id}
              className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-accent-gold/30"
            >
              {/* Card Header with gradient background */}
              <div className="h-16 bg-gradient-to-br from-gray-800 to-gray-900 relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>
              </div>

              {/* Avatar - positioned to overlap header */}
              <div className="flex justify-center -mt-10 relative z-10">
                <div
                  className={`h-20 w-20 rounded-2xl bg-gradient-to-br ${getAvatarGradient(index)} flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-4 ring-white`}
                >
                  {member.full_name?.charAt(0).toUpperCase()}
                </div>
              </div>

              {/* Content */}
              <div className="px-5 pt-4 pb-5">
                {/* Name & Role */}
                <div className="text-center mb-4">
                  <h4 className="text-lg font-bold text-gray-900 group-hover:text-accent-gold transition-colors">
                    {member.full_name}
                  </h4>
                  <p className="text-sm text-gray-500 truncate mt-0.5">{member.email}</p>
                  <div className="mt-3">{getRoleBadge(member.role)}</div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 my-4"></div>

                {/* Details */}
                <div className="space-y-2.5">
                  {member.phone && (
                    <div className="flex items-center gap-2.5 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Phone className="h-4 w-4 text-blue-500" />
                      </div>
                      <span className="text-gray-600">{member.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <Users className="h-4 w-4 text-amber-500" />
                    </div>
                    <span className="text-gray-600">
                      Joined{' '}
                      {member.branch_assignment_date
                        ? new Date(member.branch_assignment_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'Recently'}
                    </span>
                  </div>
                </div>

                {/* Status indicator */}
                <div className="mt-4 flex items-center justify-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                  <span className="text-xs font-medium text-green-600">Active</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Add Product Modal
const AddProductModal = ({ branchId, existingProductIds, onClose, onSuccess }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    try {
      const response = await getProducts({ limit: 500, isActive: true });
      setAllProducts(response.data?.products || response.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const availableProducts = allProducts.filter(
    (p) =>
      !existingProductIds.includes(p.id) &&
      (p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.product_code?.toLowerCase().includes(search.toLowerCase()))
  );

  const toggleProduct = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one product');
      return;
    }
    setSubmitting(true);
    try {
      await assignProductsToBranch(branchId, selectedIds);
      toast.success(`${selectedIds.length} product(s) assigned`);
      onSuccess();
    } catch (error) {
      toast.error('Failed to assign products');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold">Add Products to Branch</h2>
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : availableProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {search ? 'No matching products' : 'All products already assigned'}
            </div>
          ) : (
            <div className="space-y-2">
              {availableProducts.map((product) => (
                <label
                  key={product.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedIds.includes(product.id)
                      ? 'bg-accent-gold/10 border-accent-gold'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product.id)}
                    onChange={() => toggleProduct(product.id)}
                    className="w-4 h-4 text-accent-gold rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.product_code}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-between items-center">
          <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || selectedIds.length === 0}
              className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Products'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Transfer Staff Modal
const TransferStaffModal = ({ branchId, branchName, onClose, onSuccess }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAllUsers();
      // API returns { success: true, data: users[] }, so handle both formats
      const users = Array.isArray(response.data) ? response.data : response.data || response || [];
      // Filter users not in this branch
      const available = users.filter(
        (u) =>
          u.current_branch_id !== parseInt(branchId) &&
          ['admin', 'staff', 'retail_admin', 'branch_admin', 'branch_staff'].includes(u.role) &&
          !u.is_locked
      );
      setAllUsers(available);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error('Select a staff member');
      return;
    }
    setSubmitting(true);
    try {
      await transferStaff(branchId, selectedUserId, reason || `Transferred to ${branchName}`);
      toast.success('Staff transferred successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to transfer staff');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Transfer Staff to {branchName}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select Staff Member</label>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : allUsers.length === 0 ? (
                <p className="text-gray-500">No available staff to transfer</p>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Choose a staff member...</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({u.email})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Reason (optional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Promotion, rotation"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedUserId}
                className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg disabled:opacity-50"
              >
                {submitting ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Branch Modal
const EditBranchModal = ({ branch, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: branch.name || '',
    address: branch.address || '',
    phone: branch.phone || '',
    email: branch.email || '',
    isActive: branch.is_active ?? true,
    isMain: branch.is_main ?? false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Edit Branch</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((f) => ({ ...f, isActive: e.target.checked }))}
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isMain}
                  onChange={(e) => setFormData((f) => ({ ...f, isMain: e.target.checked }))}
                />
                <span className="text-sm">Main Branch</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BranchDetailPage;
